import React, {
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { RegistrationFieldProps } from '../../RegistrationField.types';
import { Box, Text, TextInput } from '@betty/moonlight';
import AutoAddressField from './AutoAddressField/AutoAddressField';
import ManualAddressField from './ManualAddressField/ManualAddressField';
import { InteractionManager, Pressable } from 'react-native';
import { useTranslation } from '@betty/i18n';
import { useRedirectSuggestion } from '../../../../hooks/useRedirectSuggestion';

function AddressField(
  { value, error, onValue }: RegistrationFieldProps<'address'>,
  _: ForwardedRef<TextInput | null>,
) {
  const addressInputRef = useRef<TextInput>(null);
  const { t } = useTranslation('registration');
  const handleToggle = useCallback(() => {
    onValue({ ...value, useAutoAddress: !value.useAutoAddress });
  }, [value, onValue]);

  useRedirectSuggestion();

  const hasErrorValues = useMemo(() => {
    return Boolean(
      error &&
        typeof error === 'object' &&
        Object.values(error).some((value) => value != null && value !== ''),
    );
  }, [error]);

  useEffect(() => {
    InteractionManager.runAfterInteractions(() => {
      addressInputRef.current?.focus();
    });
  }, []);

  if (value.useAutoAddress) {
    return (
      <Box>
        <AutoAddressField
          ref={addressInputRef}
          value={value}
          error={hasErrorValues ? t('genericAddressError') : undefined}
          onValue={onValue}
        />
        {hasErrorValues && (
          <Text color="darkSurfaceError" variant="body-m" marginTop="space-200">
            {t('genericAddressError')}
          </Text>
        )}
        <Pressable onPress={handleToggle}>
          <Box flexDirection="column" alignItems="flex-end">
            <Text
              variant="body-s"
              gradient="darkSurfaceAccent"
              locNS="registration"
              locKey="enterManually"
            />
          </Box>
        </Pressable>
      </Box>
    );
  }

  return (
    <Box>
      <ManualAddressField value={value} error={error} onValue={onValue} />
      <Pressable onPress={handleToggle}>
        <Box flexDirection="column" alignItems="flex-end">
          <Text
            variant="body-s"
            gradient="darkSurfaceAccent"
            locNS="registration"
            locKey="switchToAuto"
          />
        </Box>
      </Pressable>
    </Box>
  );
}
export default memo(forwardRef(AddressField));
